import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import adminAxiosInstance from "../utils/adminAxios";
import { API_ENDPOINTS } from "../config";
import BookChapterAnalytics from "./BookChapterAnalytics";

/**
 * Admin analytics dashboard.
 *
 * Every number here comes from a server-side aggregation — the client never
 * receives raw user/session/QnA documents, only the counts it displays. Keep
 * it that way: pulling lists down to filter in the browser would put the
 * 512MB backend straight back into the fan-out that used to OOM it.
 */

const RANGE_OPTIONS = [
  { label: "Last 7 days", value: "7" },
  { label: "Last 30 days", value: "30" },
  { label: "Last 90 days", value: "90" },
  { label: "Last 365 days", value: "365" },
  { label: "Custom range…", value: "custom" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

// The list of apps comes from the data (overview.publishers), so adding a third
// publisher needs no frontend change. These are only the fallback for the first
// render, before the overview has loaded.
const FALLBACK_PUBLISHERS = ["JD", "EPS", "BIGBOOKS"];

// Columns the backend can sort on. Anything else is rejected server-side.
const SORTABLE = {
  createdAt: "Registered",
  username: "Username",
  minutes: "Time spent",
  sessions: "Sessions",
};

function formatMinutes(mins) {
  if (!mins) return "0m";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

const StatCard = ({ label, value, sub, tone = "blue" }) => {
  const tones = {
    blue: "border-blue-500",
    green: "border-green-500",
    purple: "border-purple-500",
    amber: "border-amber-500",
    red: "border-red-500",
  };
  return (
    <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${tones[tone]}`}>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
};

const AdminAnalytics = () => {
  const navigate = useNavigate();

  const [publisher, setPublisher] = useState("");
  const [days, setDays] = useState("30");
  // Only sent when days === "custom"; the server ignores an incomplete pair.
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(todayISO());

  const [overview, setOverview] = useState(null);
  const [publisherOptions, setPublisherOptions] = useState(FALLBACK_PUBLISHERS);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [users, setUsers] = useState([]);
  const [userPage, setUserPage] = useState(1);
  const [userPagination, setUserPagination] = useState(null);
  const [sortBy, setSortBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);

  const [errors, setErrors] = useState([]);
  const [errorSummary, setErrorSummary] = useState(null);
  const [errorLevel, setErrorLevel] = useState("");
  const [expandedError, setExpandedError] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("adminToken")) navigate("/admin-login");
  }, [navigate]);

  // The server decides what this account may see; these flags only stop the
  // page rendering controls that would be refused anyway.
  const scopedPublisher = overview?.viewer?.scopedPublisher || null;
  const canViewErrors = overview?.viewer?.canViewErrors !== false;


  // ---- headline numbers + charts --------------------------------------
  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rangeParams =
        days === "custom" ? { from, to } : { days: Number(days) };
      const params = {
        ...(publisher ? { publisher } : {}),
        ...rangeParams,
      };
      const [ov, ts] = await Promise.all([
        adminAxiosInstance.get(API_ENDPOINTS.ANALYTICS_OVERVIEW, { params }),
        adminAxiosInstance.get(API_ENDPOINTS.ANALYTICS_TIMESERIES, { params }),
      ]);
      setOverview(ov.data.data);
      setSeries(ts.data.data || []);
      // Keep the previous list if the server sent none, so selecting a
      // publisher with no users cannot empty the dropdown and strand the admin.
      if (ov.data.data?.publishers?.length) {
        setPublisherOptions(ov.data.data.publishers);
      }
    } catch (e) {
      setError(
        e.response?.data?.error || e.message || "Could not load analytics"
      );
    } finally {
      setLoading(false);
    }
  }, [publisher, days, from, to]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  // ---- per-user table ---------------------------------------------------
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const { data } = await adminAxiosInstance.get(API_ENDPOINTS.ANALYTICS_USERS, {
        params: {
          page: userPage, limit: 25, sortBy, order,
          ...(search ? { search } : {}),
          ...(publisher ? { publisher } : {}),
        },
      });
      setUsers(data.data || []);
      setUserPagination(data.pagination || null);
    } catch (e) {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [userPage, sortBy, order, search, publisher]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ---- error log --------------------------------------------------------
  const loadErrors = useCallback(async () => {
    if (!canViewErrors) {
      setErrors([]);
      setErrorSummary(null);
      return;
    }
    try {
      const { data } = await adminAxiosInstance.get(API_ENDPOINTS.ANALYTICS_ERRORS, {
        params: {
          limit: 25,
          ...(errorLevel ? { level: errorLevel } : {}),
          ...(publisher ? { publisher } : {}),
        },
      });
      setErrors(data.data || []);
      setErrorSummary(data.summary || null);
    } catch (e) {
      setErrors([]);
    }
  }, [errorLevel, publisher, canViewErrors]);

  useEffect(() => { loadErrors(); }, [loadErrors]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setOrder("desc");
    }
    setUserPage(1);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setUserPage(1);
  };

  // What the server actually reported on, so the tiles cannot claim a window
  // different from the one that produced the numbers.
  const rangeLabel = overview?.range?.label || (days === "custom" ? `${from} → ${to}` : `Last ${days} days`);

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">Loading analytics…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Analytics</h1>
            <p className="text-sm text-gray-500">
              Usage, engagement and backend health
              {overview?.generatedAt && (
                <> · updated {new Date(overview.generatedAt).toLocaleTimeString()}</>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {scopedPublisher ? (
              <span className="border rounded px-3 py-2 text-sm bg-gray-100 text-gray-700">
                {scopedPublisher}
              </span>
            ) : (
              <select
                className="border rounded px-3 py-2 text-sm bg-white"
                value={publisher}
                onChange={(e) => { setPublisher(e.target.value); setUserPage(1); }}
              >
                <option value="">All apps</option>
                {publisherOptions.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            )}
            {days === "custom" && (
              <>
                <input
                  type="date"
                  className="border rounded px-2 py-2 text-sm bg-white"
                  value={from}
                  max={to}
                  onChange={(e) => setFrom(e.target.value)}
                />
                <span className="self-center text-gray-400 text-sm">to</span>
                <input
                  type="date"
                  className="border rounded px-2 py-2 text-sm bg-white"
                  value={to}
                  min={from}
                  max={todayISO()}
                  onChange={(e) => setTo(e.target.value)}
                />
              </>
            )}
            <select
              className="border rounded px-3 py-2 text-sm bg-white"
              value={days}
              onChange={(e) => setDays(e.target.value)}
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={loadOverview}
              className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
            >
              Refresh
            </button>
            {/* A scoped admin is bounced off /admin/dashboard, so "Back" would
                loop. Give them the only exit that makes sense. */}
            {scopedPublisher ? (
              <button
                onClick={() => {
                  localStorage.removeItem("adminToken");
                  localStorage.removeItem("adminRole");
                  localStorage.removeItem("adminPublisher");
                  navigate("/admin-login");
                }}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300"
              >
                Log out
              </button>
            ) : (
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300"
              >
                Back
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Numbers are cached server-side for 5 minutes; say so rather than
            letting an admin wonder why Refresh changes nothing. */}
        <p className="text-xs text-gray-400 mb-4">
          Figures are cached for up to 5 minutes.
        </p>

        {overview && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard
                label="Total users"
                value={overview.users.total.toLocaleString()}
                sub={`+${overview.users.new7d} in 7 days`}
              />
              <StatCard
                label={`Active (${rangeLabel})`}
                value={overview.active.monthly.toLocaleString()}
                sub={`${overview.active.monthlyEngagementPercent}% of all users`}
                tone="green"
              />
              <StatCard
                label="Active today"
                value={overview.active.daily.toLocaleString()}
                sub={`${overview.active.weekly} this week`}
                tone="green"
              />
              <StatCard
                label={`New (${rangeLabel})`}
                value={(overview.users.newInRange ?? overview.users.new30d).toLocaleString()}
                sub={`${overview.users.newToday} today`}
              />
              <StatCard
                label={`Time spent (${rangeLabel})`}
                value={formatMinutes(overview.time.inRange.totalMinutes)}
                sub={`${overview.time.inRange.totalSessions} sessions`}
                tone="purple"
              />
              <StatCard
                label="Study sessions"
                value={overview.time.inRange.totalSessions.toLocaleString()}
                // Learn (chat_ask) stores no timing field, so all measured
                // minutes are quiz time. Say that rather than print a blank.
                sub={`${rangeLabel} · quiz time only`}
                tone="purple"
              />
              <StatCard
                label="Questions answered"
                value={overview.questions.allTime.questionsAnswered.toLocaleString()}
                sub={`avg score ${overview.questions.allTime.averageScorePercent}%`}
                tone="amber"
              />
              <StatCard
                label="Subscriptions"
                value={(overview.subscriptions?.total ?? overview.totalSubscriptions ?? 0).toLocaleString()}
                sub={`${(overview.subscriptions?.last30Days ?? 0).toLocaleString()} in last 30 days · ${(overview.subscriptions?.distinctBooks ?? 0).toLocaleString()} distinct books`}
                tone="green"
              />
              <StatCard
                label="Chapters completed"
                value={overview.chaptersCompleted.toLocaleString()}
                sub={`${overview.chaptersCompleted === 1 ? "chapter" : "chapters"} finished end to end`}
                tone="amber"
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow p-4">
                <h2 className="font-semibold text-gray-700 mb-3">
                  Registrations &amp; active users
                </h2>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="registrations" name="New users" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="activeUsers" name="Active users" stroke="#10b981" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <h2 className="font-semibold text-gray-700 mb-3">
                  Time spent &amp; questions answered
                </h2>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="minutes" name="Minutes" fill="#8b5cf6" />
                    <Bar dataKey="questionsAnswered" name="Questions" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {/* Book-wise and chapter-wise breakdown. Scoped admins pass no
            publisher of their own — the server applies their scope. */}
        <BookChapterAnalytics publisher={scopedPublisher || publisher} />

        {/* Per-user table */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b">
            <h2 className="font-semibold text-gray-700">Users</h2>
            <form onSubmit={submitSearch} className="flex gap-2">
              <input
                className="border rounded px-3 py-1.5 text-sm"
                placeholder="Search name, username, email"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button type="submit" className="bg-gray-800 text-white px-3 py-1.5 rounded text-sm">
                Search
              </button>
            </form>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {Object.entries(SORTABLE).map(([field, label]) => (
                    <th
                      key={field}
                      onClick={() => toggleSort(field)}
                      className="text-left px-4 py-2 cursor-pointer select-none hover:text-gray-900"
                      title="Click to sort"
                    >
                      {label}{" "}
                      {sortBy === field && (order === "asc" ? "▲" : "▼")}
                    </th>
                  ))}
                  <th className="text-left px-4 py-2">Last active</th>
                  <th className="text-right px-4 py-2">Questions</th>
                  <th className="text-right px-4 py-2">Chapters done</th>
                  <th className="text-right px-4 py-2">Avg score</th>
                </tr>
              </thead>
              <tbody>
                {usersLoading && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
                )}
                {!usersLoading && users.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">No users found</td></tr>
                )}
                {!usersLoading && users.map((u) => (
                  <tr key={u.userId} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap">{formatDate(u.registeredAt)}</td>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-800">{u.username}</div>
                      <div className="text-xs text-gray-500">
                        {u.fullname}{u.publisher ? ` · ${u.publisher}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatMinutes(u.minutesSpent)}</td>
                    <td className="px-4 py-2 text-right">{u.sessions}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatDate(u.lastActiveAt)}</td>
                    <td className="px-4 py-2 text-right">{u.questionsAnswered}</td>
                    <td className="px-4 py-2 text-right">
                      {u.chaptersCompleted}
                      <span className="text-gray-400"> / {u.chaptersStarted}</span>
                    </td>
                    <td className="px-4 py-2 text-right">{u.averageScorePercent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {userPagination && userPagination.totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t text-sm">
              <span className="text-gray-500">
                Page {userPagination.page} of {userPagination.totalPages} · {userPagination.total} users
              </span>
              <div className="flex gap-2">
                <button
                  disabled={!userPagination.hasPrev}
                  onClick={() => setUserPage((p) => p - 1)}
                  className="px-3 py-1 border rounded disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  disabled={!userPagination.hasNext}
                  onClick={() => setUserPage((p) => p + 1)}
                  className="px-3 py-1 border rounded disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Backend health — full admins only. */}
        {canViewErrors && (
        <div className="bg-white rounded-lg shadow">
          <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b">
            <div>
              <h2 className="font-semibold text-gray-700">Backend errors</h2>
              {errorSummary && (
                <p className="text-xs text-gray-500 mt-1">
                  Last 7 days —{" "}
                  {Object.entries(errorSummary.last7Days).length === 0
                    ? "nothing logged"
                    : Object.entries(errorSummary.last7Days)
                        .map(([k, v]) => `${v} ${k}`)
                        .join(", ")}
                </p>
              )}
            </div>
            <select
              className="border rounded px-3 py-1.5 text-sm bg-white"
              value={errorLevel}
              onChange={(e) => setErrorLevel(e.target.value)}
            >
              <option value="">All levels</option>
              <option value="fatal">Fatal</option>
              <option value="error">Error</option>
              <option value="memory">Memory warning</option>
              <option value="warn">Warning</option>
            </select>
          </div>

          {/* An OOM kill cannot log itself — explain the gap rather than let it
              look like the monitoring is broken. */}
          <p className="px-4 pt-3 text-xs text-gray-500">
            Memory warnings are recorded before the container runs out of RAM. If the
            service restarts with no fatal entry, look at the last memory rows — an
            out-of-memory kill cannot write its own log entry.
            {publisher && (
              <> Filtered to <strong>{publisher}</strong>, plus crashes and memory
              warnings that carry no app (those affect every app).</>
            )}
          </p>

          <div className="overflow-x-auto p-4">
            {errors.length === 0 ? (
              <p className="text-gray-400 text-sm py-4 text-center">No errors logged</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Level</th>
                    <th className="text-left px-3 py-2">Route</th>
                    <th className="text-left px-3 py-2">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((e) => (
                    <React.Fragment key={e._id}>
                      <tr
                        className="border-t hover:bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedError(expandedError === e._id ? null : e._id)}
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                          {new Date(e.createdAt).toLocaleString()}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              e.level === "fatal" ? "bg-red-100 text-red-700"
                                : e.level === "memory" ? "bg-amber-100 text-amber-700"
                                : e.level === "warn" ? "bg-yellow-100 text-yellow-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {e.level}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {e.method ? `${e.method} ` : ""}{e.path || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-800">{e.message}</td>
                      </tr>
                      {expandedError === e._id && e.stack && (
                        <tr className="bg-gray-50">
                          <td colSpan={4} className="px-3 py-2">
                            <pre className="text-xs overflow-x-auto whitespace-pre-wrap text-gray-600">
                              {e.stack}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
